import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whitelistApi, type WhitelistEntry, type WhitelistFilter } from '../api/whitelist.js';
import { Spinner, ErrorBox, EmptyBox, Field, SearchInput } from '../components/ui.js';
import { datetime } from '../lib/format.js';

/**
 * 관리자 접속 IP 화이트리스트 (원본 xadmin/whiteip_list.php, whiteip_add.php,
 * whiteip_modify.php - 모두 banana_custom.admin_whitelist 하나를 본다).
 *
 * 원본은 home.php 메뉴에 없어서 URL 을 아는 사람만 썼다 - menu.ts '설정'
 * 섹션(xadmin 전용, settingsAccess.ts 참고)에만 둔다.
 *
 * 이 표가 곧 middleware/ipWhitelist.ts 가 관리자 화면 전체를 여닫는 데 쓰는
 * 그 목록이다. 잘못 끄면 자기 자신도 잠길 수 있어서, 서버(whitelist.service.ts)
 * 가 "지금 이 접속을 막는 항목이 이것뿐이면 비활성화 거부"를 강제한다 - 여기서
 * 따로 막지 않고 그 에러 메시지를 그대로 보여준다.
 */

const IPTYPE_LABEL: Record<1 | 2 | 3, string> = {
  1: 'IP 전체일치',
  2: 'D범위 허용 (/24)',
  3: 'C,D범위 허용 (/16)',
};

const IPTYPE_HINT: Record<1 | 2 | 3, string> = {
  1: '1.2.3.4',
  2: '1.2.3.*',
  3: '1.2.*.*',
};

const FILTER_TABS: { value: WhitelistFilter; label: string }[] = [
  { value: 'N', label: '사용중' },
  { value: 'Y', label: '사용안함' },
  { value: 'A', label: '전체' },
];

interface EditForm {
  memo: string;
  isActive: boolean;
}

export function WhitelistPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<WhitelistFilter>('N');
  const [search, setSearch] = useState('');

  const [ip, setIp] = useState('');
  const [iptype, setIptype] = useState<1 | 2 | 3>(1);
  const [isActive, setIsActive] = useState(true);
  const [memo, setMemo] = useState('');

  const [editingSeq, setEditingSeq] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);

  const list = useQuery({
    queryKey: ['whitelist', filter, search],
    queryFn: () => whitelistApi.list(filter, search || undefined),
  });

  const create = useMutation({
    mutationFn: () => whitelistApi.create({ ip: ip.trim(), iptype, memo: memo.trim(), isActive }),
    onSuccess: () => {
      setIp('');
      setMemo('');
      void qc.invalidateQueries({ queryKey: ['whitelist'] });
    },
  });

  const update = useMutation({
    mutationFn: (seq: number) => whitelistApi.update(seq, { memo: editForm!.memo, isActive: editForm!.isActive }),
    onSuccess: () => {
      setEditingSeq(null);
      setEditForm(null);
      void qc.invalidateQueries({ queryKey: ['whitelist'] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (create.isPending) return;
    create.mutate();
  }

  function startEdit(entry: WhitelistEntry) {
    update.reset();
    setEditingSeq(entry.seq);
    setEditForm({ memo: entry.memo ?? '', isActive: entry.isActive });
  }

  function cancelEdit() {
    setEditingSeq(null);
    setEditForm(null);
  }

  function saveEdit(e: FormEvent, seq: number) {
    e.preventDefault();
    if (update.isPending || !editForm) return;
    update.mutate(seq);
  }

  const entries = list.data?.entries ?? [];
  const showDeleteCols = filter !== 'N';

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            화이트 IP 관리
            <span className="badge badge--danger">admin 전용</span>
          </h1>
          <p className="page-desc">
            여기 등록된 IP 에서만 관리자 화면에 접속할 수 있습니다. 잘못 비활성화하면 본인을 포함해
            전체 관리자가 화면에서 막힐 수 있어, 지금 접속 중인 IP 를 유일하게 허용하는 항목은
            비활성화가 거부됩니다.
          </p>
        </div>
      </header>

      <div className="panel">
        <form className="form-grid" onSubmit={submit}>
          <Field label="등록할 아이피">
            <input
              className="input"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="1.2.3.4"
              maxLength={50}
              required
            />
          </Field>
          <Field label="IP 범위" full as="div">
            <div className="checkbox-group">
              {([1, 2, 3] as const).map((t) => (
                <label className="checkbox" key={t}>
                  <input
                    type="radio"
                    name="iptype"
                    checked={iptype === t}
                    onChange={() => setIptype(t)}
                  />
                  {IPTYPE_LABEL[t]} ({IPTYPE_HINT[t]})
                </label>
              ))}
            </div>
          </Field>
          <Field label="상태" as="div">
            <div className="checkbox-group">
              <label className="checkbox">
                <input type="radio" name="isActive" checked={isActive} onChange={() => setIsActive(true)} />
                허용
              </label>
              <label className="checkbox">
                <input type="radio" name="isActive" checked={!isActive} onChange={() => setIsActive(false)} />
                차단
              </label>
            </div>
          </Field>
          <Field label="비고 (추가 사유, 5자 이상)" full>
            <input
              className="input"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              minLength={5}
              maxLength={255}
              required
            />
          </Field>

          <button className="btn" type="submit" disabled={create.isPending}>
            {create.isPending ? '등록 중…' : 'IP 추가'}
          </button>
        </form>

        {create.isError && <ErrorBox error={create.error} />}
        {create.isSuccess && (
          <p className="panel__note">
            <strong>{create.data.entry.ip}</strong> 을(를) 등록했습니다.
          </p>
        )}
      </div>

      <div className="panel">
        <div className="tab-group" aria-label="사용 여부">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={filter === tab.value ? 'tab-btn tab-btn--active' : 'tab-btn'}
              onClick={() => setFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="아이피 또는 메모로 검색" />
        </div>

        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
        ) : entries.length === 0 ? (
          <EmptyBox>등록된 IP 가 없습니다.</EmptyBox>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>IP</th>
                  <th>범위</th>
                  <th style={{ textAlign: 'center' }}>상태</th>
                  <th>등록자IP</th>
                  <th>비고</th>
                  <th>등록일</th>
                  {showDeleteCols && (
                    <>
                      <th>삭제자IP</th>
                      <th>삭제일</th>
                    </>
                  )}
                  <th style={{ textAlign: 'center' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) =>
                  editingSeq === entry.seq && editForm ? (
                    <tr key={entry.seq}>
                      <td colSpan={showDeleteCols ? 8 : 6}>
                        <form className="form-grid" onSubmit={(e) => saveEdit(e, entry.seq)}>
                          <Field label="아이피">
                            <input className="input" value={entry.ip} disabled readOnly />
                          </Field>
                          <Field label="상태" as="div">
                            <div className="checkbox-group">
                              <label className="checkbox">
                                <input
                                  type="radio"
                                  name={`edit-isActive-${entry.seq}`}
                                  checked={editForm.isActive}
                                  onChange={() =>
                                    setEditForm((prev) => (prev ? { ...prev, isActive: true } : prev))
                                  }
                                />
                                허용
                              </label>
                              <label className="checkbox">
                                <input
                                  type="radio"
                                  name={`edit-isActive-${entry.seq}`}
                                  checked={!editForm.isActive}
                                  onChange={() =>
                                    setEditForm((prev) => (prev ? { ...prev, isActive: false } : prev))
                                  }
                                />
                                차단
                              </label>
                            </div>
                          </Field>
                          <Field label="비고 (5자 이상)" full>
                            <input
                              className="input"
                              value={editForm.memo}
                              onChange={(e) =>
                                setEditForm((prev) => (prev ? { ...prev, memo: e.target.value } : prev))
                              }
                              minLength={5}
                              maxLength={255}
                              required
                            />
                          </Field>

                          <div className="editor__actions">
                            <button className="btn" type="submit" disabled={update.isPending}>
                              {update.isPending ? '저장 중…' : '저장'}
                            </button>
                            <button
                              className="btn btn--ghost"
                              type="button"
                              onClick={cancelEdit}
                              disabled={update.isPending}
                            >
                              취소
                            </button>
                          </div>
                          {update.isError && <ErrorBox error={update.error} />}
                        </form>
                      </td>
                    </tr>
                  ) : (
                    <tr key={entry.seq}>
                      <td>{entry.ip}</td>
                      <td className="td-muted">{IPTYPE_LABEL[entry.iptype]}</td>
                      <td style={{ textAlign: 'center' }}>
                        {entry.isActive ? (
                          <span className="badge badge--warn">사용중</span>
                        ) : (
                          <span className="td-muted">비활성</span>
                        )}
                      </td>
                      <td className="td-muted">{entry.createdIp ?? '-'}</td>
                      <td>{entry.memo ?? '-'}</td>
                      <td className="td-muted">{entry.createdAt ? datetime(entry.createdAt) : '-'}</td>
                      {showDeleteCols && (
                        <>
                          <td className="td-muted">{entry.deletedIp ?? '-'}</td>
                          <td className="td-muted">{entry.deletedAt ? datetime(entry.deletedAt) : '-'}</td>
                        </>
                      )}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          onClick={() => startEdit(entry)}
                        >
                          수정
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
